import type { SettingDefinition, SettingGroupDefinition } from "./types";
import { validateSettingsProvider } from "./validation";

export interface SettingProvider<
  Groups extends
    readonly SettingGroupDefinition[] = readonly SettingGroupDefinition[],
  Definitions extends readonly SettingDefinition<
    string,
    Groups[number]["id"]
  >[] = readonly SettingDefinition<string, Groups[number]["id"]>[],
> {
  name: string;
  groups: Groups;
  definitions: Definitions;
  groupById: Readonly<Record<Groups[number]["id"], Groups[number]>>;
  definitionById: Readonly<
    Record<Definitions[number]["id"], Definitions[number]>
  >;
}

export function defineSettingsProvider<
  const Groups extends readonly SettingGroupDefinition[],
  const Definitions extends readonly SettingDefinition<
    string,
    Groups[number]["id"]
  >[],
>({
  name,
  groups,
  definitions,
}: {
  name: string;
  groups: Groups;
  definitions: Definitions;
}): SettingProvider<Groups, Definitions> {
  const groupById = indexById(`${name} setting group`, groups);
  const definitionById = indexById(`${name} setting`, definitions);

  validateSettingsProvider(name, groups, definitions, groupById);

  return { name, groups, definitions, groupById, definitionById };
}

function indexById<const Items extends readonly { id: string }[]>(
  kind: string,
  items: Items,
): Readonly<Record<Items[number]["id"], Items[number]>> {
  const index: Record<string, Items[number]> = {};

  for (const item of items) {
    if (index[item.id]) {
      throw new Error(`Duplicate ${kind} id ${item.id}`);
    }
    index[item.id] = item;
  }

  return index as Readonly<Record<Items[number]["id"], Items[number]>>;
}
